"use client"

import * as Sentry from "@sentry/nextjs"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import type { ReactNode } from "react"
import { useEffect, useRef } from "react"
import { Button } from "~/components/legacy/common/button"
import { Link } from "~/components/legacy/common/link"
import { Stack } from "~/components/legacy/common/stack"
import { RotateCwIcon } from "~/components/ui/icons"
import { Intro, IntroDescription, IntroTitle } from "~/components/legacy/web/ui/intro"
import { siteConfig } from "~/config/site"
import { attemptChunkReload } from "~/lib/chunk-load-error"
import { currentLocationHrefWithoutQuery } from "~/lib/sentry-url"

type ErrorProps = Readonly<{
  error: Error & { digest?: string }
}>

const renderSupportEmailLink = (chunks: ReactNode) => (
  <Link href={`mailto:${siteConfig.email}`}>{chunks}</Link>
)

export default function WebRouteError({ error }: ErrorProps) {
  const t = useTranslations("pages.error")
  const router = useRouter()
  const reloadedRef = useRef(false)

  useEffect(() => {
    if (reloadedRef.current) return
    if (attemptChunkReload(error)) {
      reloadedRef.current = true
    }
  }, [error])

  useEffect(() => {
    Sentry.captureException(error, {
      tags: {
        boundary: "web-route-error",
      },
      extra: {
        digest: error.digest,
        href: currentLocationHrefWithoutQuery(),
      },
    })
  }, [error])

  return (
    <Intro alignment="center">
      <IntroTitle>{t("title")}</IntroTitle>

      <IntroDescription className="max-w-xl">
        {t.rich("description", {
          link: renderSupportEmailLink,
        })}
      </IntroDescription>

      <Stack className="mt-4">
        <Button variant="fancy" onClick={() => router.refresh()} prefix={<RotateCwIcon />}>
          {t("reload_button")}
        </Button>

        <Button variant="soft" asChild>
          <Link href="/">{t("home_button")}</Link>
        </Button>
      </Stack>
    </Intro>
  )
}
